import React, { useState, useMemo, useEffect } from "react";
import { Invoice, UserRole, DateRange, User } from "../types";
import {
  formatCurrency,
  formatDate,
  checkServiceTypes,
  updateInvoice,
  deleteInvoice,
  getCurrentFactorValue,
} from "../services/firebaseService";
import InvoiceActions from "./InvoiceActions";
import "./InvoiceTable.css";

interface InvoiceTableProps {
  invoices: Invoice[];
  selectedTeam: string;
  loading: boolean;
  userRole: UserRole;
  dateRange: DateRange;
  onDateRangeChange: (dateRange: DateRange) => void;
  onRefresh: () => void;
  userData?: User;
}

const InvoiceTable: React.FC<InvoiceTableProps> = ({
  invoices,
  selectedTeam,
  loading,
  userRole,
  dateRange,
  onDateRangeChange,
  onRefresh,
  userData,
}) => {
  const [filterStatus, setFilterStatus] = useState<
    "all" | "checked" | "unchecked"
  >("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedInvoices, setSelectedInvoices] = useState<Set<string>>(
    new Set(),
  );
  const [bulkActionsVisible, setBulkActionsVisible] = useState(false);

  // Set initial filter status based on user role
  useEffect(() => {
    if (userRole === "superv") {
      setFilterStatus("unchecked"); // "Não corrigidos" for supervisors
    } else if (userRole === "admin") {
      setFilterStatus("checked"); // "Corrigidos pela supervisão" for admins
    }
  }, [userRole]);

  const filteredInvoices = useMemo(() => {
    return invoices.filter((invoice) => {
      const matchesFilter =
        filterStatus === "all" ||
        (filterStatus === "checked" && invoice.checked) ||
        (filterStatus === "unchecked" && !invoice.checked);

      const titleForSearch = (
        invoice.header?.title ||
        invoice.title ||
        ""
      ).toLowerCase();
      const matchesSearch =
        searchTerm === "" ||
        invoice.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        titleForSearch.includes(searchTerm.toLowerCase()) ||
        invoice.project.toLowerCase().includes(searchTerm.toLowerCase()) ||
        invoice.city.toLowerCase().includes(searchTerm.toLowerCase());

      return matchesFilter && matchesSearch;
    });
  }, [invoices, filterStatus, searchTerm]);

  const handleInvoiceSelect = (invoiceKey: string, selected: boolean) => {
    const newSelected = new Set(selectedInvoices);
    if (selected) {
      newSelected.add(invoiceKey);
    } else {
      newSelected.delete(invoiceKey);
    }
    setSelectedInvoices(newSelected);
    setBulkActionsVisible(newSelected.size > 0);
  };

  const handleSelectAll = (selected: boolean) => {
    if (selected) {
      const allKeys = new Set(filteredInvoices.map((inv) => inv.key));
      setSelectedInvoices(allKeys);
      setBulkActionsVisible(true);
    } else {
      setSelectedInvoices(new Set());
      setBulkActionsVisible(false);
    }
  };

  const handleBulkAction = async (action: "viewed" | "paid" | "attached") => {
    const selectedKeys = Array.from(selectedInvoices);
    const actionText = {
      viewed: "marcar como vistos",
      paid: "marcar como pagos",
      attached: "dar baixa",
    }[action];

    if (window.confirm(`Deseja ${actionText} nos boletins selecionados?`)) {
      try {
        const promises = selectedKeys.map(async (key) => {
          const invoice = invoices.find((inv) => inv.key === key);
          if (invoice) {
            const updates: any = {};
            if (action === "viewed") {
              updates.viewedByFat = !invoice.viewedByFat;
            } else if (action === "paid") {
              updates.paid = !invoice.paid;
            } else if (action === "attached") {
              const pedido = window.prompt("Digite o número do pedido:");
              if (pedido) {
                updates.attached = true;
                updates.header = { ...invoice.header, pedido };
              } else {
                return;
              }
            }
            await updateInvoice(selectedTeam, key, updates);
          }
        });

        await Promise.all(promises);
        setSelectedInvoices(new Set());
        setBulkActionsVisible(false);
        onRefresh();
      } catch (error) {
        console.error("Error performing bulk action:", error);
        alert("Erro ao executar ação em massa.");
      }
    }
  };

  const handleEditInvoice = (invoice: Invoice) => {
    sessionStorage.setItem("invoiceData", JSON.stringify(invoice));
    sessionStorage.setItem("invoiceKey", invoice.key);
    if (userData) {
      sessionStorage.setItem("currentUserData", JSON.stringify(userData));
    }
    window.open("/edit", "_blank");
  };

  const handleDeleteInvoice = async (invoice: Invoice) => {
    if (window.confirm("Deseja deletar este boletim?")) {
      try {
        await deleteInvoice(selectedTeam, invoice.key);
        onRefresh();
      } catch (error) {
        console.error("Error deleting invoice:", error);
        alert("Erro ao deletar boletim.");
      }
    }
  };

  const handleViewPDF = (invoice: Invoice) => {
    sessionStorage.setItem("invoiceData", JSON.stringify(invoice));
    sessionStorage.setItem("invoiceKey", invoice.key);
    window.open(
      "/pdf",
      "_blank",
      "width=1200,height=800,scrollbars=yes,resizable=yes",
    );
  };

  const handleShowKey = (invoice: Invoice) => {
    if (userRole !== "admin") return;

    const teamId = invoice.team || selectedTeam;
    const path = `root/forms/${teamId}/${invoice.key}`;

    const copyWithTextareaFallback = (text: string): boolean => {
      try {
        const textarea = document.createElement("textarea");
        textarea.value = text;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "fixed";
        textarea.style.opacity = "0";
        document.body.appendChild(textarea);
        textarea.select();
        const ok = document.execCommand("copy");
        document.body.removeChild(textarea);
        return ok;
      } catch {
        return false;
      }
    };

    const navAny: any = navigator as any;
    if (navAny?.clipboard?.writeText) {
      navAny.clipboard.writeText(path).catch(() => {
        copyWithTextareaFallback(path);
      });
    } else {
      copyWithTextareaFallback(path);
    }

    // Always show a prompt so the user can copy manually if desired
    window.prompt("Caminho do formulário (copie com Ctrl+C):", path);
  };

  const getStatusBadge = (invoice: Invoice) => {
    if (invoice.attached && invoice.paid) {
      return (
        <div className="status-badges">
          <span className="status-badge paid">Pago</span>
          <span className="status-badge attached">A</span>
        </div>
      );
    }
    if (invoice.attached)
      return <span className="status-badge attached">{invoice.id}</span>;
    if (invoice.paid)
      return <span className="status-badge paid">{invoice.id}</span>;
    if (invoice.viewedByFat)
      return <span className="status-badge viewed">{invoice.id}</span>;
    return <span className="status-badge default">{invoice.id}</span>;
  };

  const hasInvalidServices = (invoice: Invoice) => {
    return !checkServiceTypes(invoice.services);
  };

  const computeDisplayTotal = (services: any): number => {
    if (!services) return 0;

    const currentFactor = getCurrentFactorValue();

    // normalize into array
    const arr = Array.isArray(services) ? services : Object.values(services);

    return arr.reduce((sum, s: any) => {
      const price = Number(s?.price) || 0;
      const qty = Number(s?.quantity) || 0;
      const fParsed = Number(s?.factor);
      const factor =
        Number.isFinite(fParsed) && fParsed > 0 ? fParsed : currentFactor;
      return sum + price * qty * factor;
    }, 0);
  };

  if (loading) {
    return (
      <div className="invoice-table-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p>Carregando boletins...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="invoice-table-container">
      <div className="table-header">
        <h2>Boletins da Equipe: {selectedTeam}</h2>

        <div className="table-controls">
          <div className="filter-controls">
            <div className="filter-group">
              <label>
                <input
                  type="radio"
                  name="status-filter"
                  value="checked"
                  checked={filterStatus === "checked"}
                  onChange={(e) => setFilterStatus(e.target.value as any)}
                />
                Corrigidos pela Supervisão
              </label>
              <label>
                <input
                  type="radio"
                  name="status-filter"
                  value="unchecked"
                  checked={filterStatus === "unchecked"}
                  onChange={(e) => setFilterStatus(e.target.value as any)}
                />
                Não Corrigidos
              </label>
            </div>

            <div className="search-controls">
              <input
                type="text"
                placeholder="Pesquisar..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
          </div>

          <div className="date-controls">
            <span>Período de Faturamento:</span>
            <input
              type="date"
              value={dateRange.startDate}
              onChange={(e) =>
                onDateRangeChange({ ...dateRange, startDate: e.target.value })
              }
              className="date-input"
            />
            <input
              type="date"
              value={dateRange.endDate}
              onChange={(e) =>
                onDateRangeChange({ ...dateRange, endDate: e.target.value })
              }
              className="date-input"
            />
            <button onClick={onRefresh} className="refresh-btn">
              <i className="fas fa-search"></i>
              Pesquisar
            </button>
          </div>
        </div>

        {userRole === "admin" && bulkActionsVisible && (
          <div className="bulk-actions">
            <button
              onClick={() => handleBulkAction("viewed")}
              className="bulk-btn viewed"
            >
              Marcar como Visto
            </button>
            <button
              onClick={() => handleBulkAction("paid")}
              className="bulk-btn paid"
            >
              Marcar como Pago
            </button>
            <button
              onClick={() => handleBulkAction("attached")}
              className="bulk-btn attached"
            >
              Dar Baixa
            </button>
          </div>
        )}

        <div className="legend">
          <div className="legend-item">
            <span className="status-badge viewed">Vistos</span>
          </div>
          <div className="legend-item">
            <span className="status-badge paid">Pagos</span>
          </div>
          <div className="legend-item">
            <span className="status-badge attached">Anexados</span>
          </div>
          {(userRole === "admin" || userRole === "superv") && (
            <div className="legend-item">
              <span className="status-badge invalid">Códigos Mistos</span>
            </div>
          )}
        </div>
      </div>

      <div className="table-wrapper">
        <table className="invoice-table">
          <thead>
            <tr>
              {userRole === "admin" && (
                <th>
                  <input
                    type="checkbox"
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    checked={
                      selectedInvoices.size === filteredInvoices.length &&
                      filteredInvoices.length > 0
                    }
                  />
                </th>
              )}
              <th>ID</th>
              <th>Projeto</th>
              <th>Título</th>
              <th>PMS</th>
              <th>OS</th>
              <th>Município</th>
              <th>Data</th>
              <th>Valor</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filteredInvoices.map((invoice) => (
              <tr
                key={invoice.key}
                className={
                  hasInvalidServices(invoice) ? "invalid-services" : ""
                }
              >
                {userRole === "admin" && (
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedInvoices.has(invoice.key)}
                      onChange={(e) =>
                        handleInvoiceSelect(invoice.key, e.target.checked)
                      }
                    />
                  </td>
                )}
                <td
                  {...(userRole === "admin"
                    ? {
                        onDoubleClick: () => handleShowKey(invoice),
                        title:
                          "Clique duas vezes para copiar o caminho do formulário",
                        style: { cursor: "copy" as const },
                      }
                    : {})}
                >
                  {getStatusBadge(invoice)}
                </td>
                <td>
                  {hasInvalidServices(invoice) ? (
                    <span className="invalid-project">{invoice.project}</span>
                  ) : (
                    invoice.project
                  )}
                </td>
                <td
                  className="title-cell"
                  title={invoice.header?.title || invoice.title}
                >
                  {invoice.header?.title || invoice.title}
                </td>
                <td className="pms-cell" title={invoice.pms}>
                  {invoice.pms}
                </td>
                <td className="os-cell" title={invoice.os}>
                  {invoice.os}
                </td>
                <td className="city-cell" title={invoice.city}>
                  {invoice.city}
                </td>
                <td>{formatDate(invoice.date)}</td>
                <td className="value-cell">
                  {formatCurrency(computeDisplayTotal(invoice.services))}
                </td>
                <td>
                  <InvoiceActions
                    invoice={invoice}
                    userRole={userRole}
                    onEdit={() => handleEditInvoice(invoice)}
                    onDelete={() => handleDeleteInvoice(invoice)}
                    onViewPDF={() => handleViewPDF(invoice)}
                    onRefresh={onRefresh}
                    selectedTeam={selectedTeam}
                    userData={userData}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {filteredInvoices.length === 0 && !loading && (
          <div className="empty-state">
            <i className="fas fa-inbox"></i>
            <h3>Nenhum boletim encontrado</h3>
            <p>Não há boletins para o período e filtros selecionados.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default InvoiceTable;
